// Shared helpers for the modal-scoped custom-font injection.
//
// Each modal (consent-center, privacy-center, …) receives its font config in a
// different shape from the backend. Rather than duplicate the extraction +
// CSS-rule building per modal, callers normalize their entries into
// [{ weight, url }] via collectFontWeights, read the client-configured family
// name via collectFontName, and hand both to buildFontFaceRule along with the
// modal's scope selectors.
//
// The family name is always the backend's FONT_NAME — there is no synthetic or
// hardcoded default. No usable FONT_NAME means no rule, and the modal keeps
// rendering in the component library's own font.

const WEIGHT_PROPERTY_RE = /^WEIGHT_(\d{3})$/;
// The family name has been configured under more than one key; accept either
// rather than silently rendering no font because of a naming mismatch.
const FONT_NAME_PROPERTIES = ['FONT_NAME', 'FONT_FAMILY'];

// The family name is interpolated into a CSS string literal, so reject anything
// that could terminate that string or the surrounding declaration. Blocklist
// (rather than a charset whitelist) so legitimate non-ASCII family names still
// pass through.
// eslint-disable-next-line no-control-regex
const UNSAFE_FAMILY_NAME_RE = /["'\\;{}<>]|[\u0000-\u001f]/;

export const isSafeFontFamilyName = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return !!trimmed && trimmed.length <= 64 && !UNSAFE_FAMILY_NAME_RE.test(trimmed);
};

// URL is concatenated into a CSS `url("…")` call, so require https and reject
// characters that could break out of the surrounding string.
const isSafeFontUrl = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!/^https:\/\//.test(trimmed)) return false;
  if (/["\\)]/.test(trimmed)) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(trimmed);
    return true;
  } catch {
    return false;
  }
};

// Normalize heterogeneous entries into [{ weight, url }]. getKey/getValue let
// callers adapt to their data shape without a preprocessing pass.
//   consentUI.cssStyleProperties -> { propertyName, propertyValue }
//   configDetails.PRIVACY_CENTER -> { configKey, content }
export const collectFontWeights = (entries, getKey, getValue) => (entries || [])
  .map((entry) => {
    const match = WEIGHT_PROPERTY_RE.exec(getKey(entry) || '');
    if (!match) return null;
    const url = getValue(entry);
    if (!isSafeFontUrl(url)) return null;
    return { weight: parseInt(match[1], 10), url: String(url).trim() };
  })
  .filter(Boolean);

// The configured value isn't always a bare family name — it can arrive as a CSS
// font-family value ("'Caveat', sans-serif"). Take the first family and drop the
// wrapping quotes, so both forms land on the same name and neither trips the
// quote check in isSafeFontFamilyName.
const normalizeFamilyName = (value) => (
  typeof value === 'string'
    ? value.split(',')[0].trim().replace(/^['"]/, '').replace(/['"]$/, '').trim()
    : ''
);

// Pull the client-configured family name out of the same entry list the weights
// come from, using the caller's getKey/getValue accessors. Returns null when the
// entry is missing or unsafe — buildFontFaceRule then renders nothing rather
// than inventing a name.
// A configured family name (bare or CSS font-family value) → safe bare name, or null.
export const toSafeFontFamilyName = (value) => {
  const name = normalizeFamilyName(value);
  return isSafeFontFamilyName(name) ? name : null;
};

export const collectFontName = (entries, getKey, getValue) => {
  const entry = (entries || []).find((item) => FONT_NAME_PROPERTIES.includes(getKey(item)));
  return entry ? toSafeFontFamilyName(getValue(entry)) : null;
};

// Build the full <style>-ready CSS: @font-face blocks for the client's family
// plus scope-restricted apply rules. `*` selector + !important ensures library
// components (Typography, Button, etc.) that set their own font-family are
// overridden.
//
// scopeSelectors accepts a string or an array — pass multiple scopes when the
// modal has portaled children (e.g. Popover content) that render outside the
// modal DOM subtree.
//
// The apply rule lists the family on its own — no `var(--font-family-base)`
// tail, because CustomProvider already sets that variable to this same backend
// name, and repeating it just makes the computed style read "Name, Name".
//
// font-display is `swap`, deliberately: content renders immediately in the
// library's font and re-paints once the custom face lands. On a first open —
// where there's no cached config and nothing in the HTTP cache — that swap is
// visible, and we accept it rather than hold text back. `block` would trade it
// for invisible text, which is the same delay wearing a different hat.
//
// Returns '' when there's nothing to render: no weights, no scopes, or no
// usable family name.
export const buildFontFaceRule = ({ weights, familyName, scopeSelectors }) => {
  const scopes = Array.isArray(scopeSelectors) ? scopeSelectors : [scopeSelectors];
  const validScopes = scopes.filter((s) => typeof s === 'string' && s.trim());
  // Re-check the name here too, so an unsanitized value can never reach the CSS
  // regardless of how the caller obtained it.
  const family = isSafeFontFamilyName(familyName) ? familyName.trim() : '';
  const canRender = family && validScopes.length > 0
    && Array.isArray(weights) && weights.length > 0;
  let rule = '';

  if (canRender) {
    const faces = weights.map((w) => (
      `@font-face { font-family: "${family}"; src: url("${w.url}") format("woff2"); `
      + `font-weight: ${w.weight}; font-style: normal; font-display: swap; }`
    )).join(' ');
    // Prefix with `html body` to bump specificity above any library rule that
    // uses `body .something` selectors — still just class-level, still safe.
    const applySelector = validScopes
      .flatMap((s) => [`html body ${s}`, `html body ${s} *`])
      .join(', ');
    rule = `${faces} ${applySelector} { font-family: "${family}" !important; }`;
  }

  return rule;
};
